/* Shared Experience Explorer. Only deliberate tab interactions select chapters.
   Source components mount once and suspend when inactive or outside the viewport. */
const reduce=matchMedia('(prefers-reduced-motion: reduce)');
const explorers=new Map();
let gsapPromise;
const clamp=(n,min,max)=>Math.max(min,Math.min(max,n));
/* Interaction cue: a small cursor (or touch point on touch screens) that demonstrates the input an
   automated chapter is responding to. Lives in the light DOM above the isolated source scene.
   Every method returns a GSAP tween/timeline so chapters place cues inside their own sequence and
   pause, resume and replay them together. */
const coarse=matchMedia('(hover: none) and (pointer: coarse)');
function createCue(panel,host,gsap){
 const el=document.createElement('div');
 el.className='oe-cue';el.setAttribute('aria-hidden','true');
 el.innerHTML='<span class="oe-cue__dot"><span class="oe-cue__ripple"></span><svg class="oe-cue__arrow" viewBox="0 0 24 24"><path d="M4.5 2.5 19 13.2l-6.4.9-3.6 6.4z"/></svg><span class="oe-cue__finger"></span><span class="oe-cue__label"></span></span><span class="oe-cue__wheel"><i></i></span><span class="oe-cue__rail"><i></i></span>';
 panel.append(el);
 const dot=el.querySelector('.oe-cue__dot'),ripple=el.querySelector('.oe-cue__ripple'),label=el.querySelector('.oe-cue__label'),wheel=el.querySelector('.oe-cue__wheel'),rail=el.querySelector('.oe-cue__rail i');
 const touch=()=>coarse.matches||host.clientWidth<600;
 const mode=()=>{el.dataset.mode=touch()?'touch':'mouse';};
 const at=(p)=>typeof p==='function'?p():p;
 const api={
  get touch(){return touch();},
  /* Point inside the scene (fx/fy are fractions of the element box), relative to the stage. */
  pt(node,fx=.5,fy=.5){const h=host.getBoundingClientRect(),r=node.getBoundingClientRect();return{x:r.left-h.left+r.width*fx,y:r.top-h.top+r.height*fy};},
  frac(fx,fy){return{x:host.clientWidth*fx,y:host.clientHeight*fy};},
  reset(){mode();gsap.killTweensOf([dot,ripple,wheel,rail]);gsap.set(dot,{autoAlpha:0,scale:1});gsap.set(ripple,{autoAlpha:0,scale:.4});gsap.set(wheel,{autoAlpha:0});gsap.set(el.querySelector('.oe-cue__rail'),{autoAlpha:0});el.classList.remove('is-pressed');},
  show(p,text=''){return gsap.timeline().call(()=>{mode();label.textContent=text;label.hidden=!text;}).set(dot,{x:()=>at(p).x,y:()=>at(p).y,scale:.9}).to(dot,{autoAlpha:1,scale:1,duration:.3,ease:'power2.out'});},
  move(p,duration=.8){return gsap.to(dot,{x:()=>at(p).x,y:()=>at(p).y,duration,ease:'power2.inOut'});},
  text(t){return gsap.timeline().call(()=>{label.textContent=t;label.hidden=!t;});},
  press(){return gsap.timeline().call(()=>el.classList.add('is-pressed')).to(dot,{scale:.86,duration:.12,ease:'power2.out'}).fromTo(ripple,{autoAlpha:.9,scale:.4},{autoAlpha:0,scale:1.6,duration:.55,ease:'power2.out'},0);},
  release(){return gsap.timeline().call(()=>el.classList.remove('is-pressed')).to(dot,{scale:1,duration:.18,ease:'power2.out'});},
  click(){return gsap.timeline().add(api.press()).add(api.release(),.16);},
  /* Press, travel and release: the drag/swipe a chapter is mirroring. */
  drag(from,to,duration=.9,text,onMid){const tl=gsap.timeline().add(api.show(from,text||(touch()?'Swipe':'Drag'))).add(api.press(),'+=.12').add(api.move(to,duration),'>-.05');if(onMid)tl.call(onMid,null,'<+='+(duration*.55));return tl.add(api.release()).add(api.hide(),'+=.15');},
  hide(){return gsap.to(dot,{autoAlpha:0,scale:.9,duration:.3,ease:'power2.in'});},
  /* Scroll indicator for the homepage journey: wheel hint plus a progress rail. */
  scrolling(on){return gsap.to(wheel,{autoAlpha:on?1:0,duration:.3});},
  rail(on){return gsap.to(el.querySelector('.oe-cue__rail'),{autoAlpha:on?1:0,duration:.4});},
  progress(p){gsap.set(rail,{yPercent:p*300});},
  destroy(){gsap.killTweensOf([dot,ripple,wheel,rail]);el.remove();}
 };
 api.reset();
 return api;
}
function createMoment(host,assets){
 const cleanups=[];
 let controller,root,loading,visible=false,finished=false,dead=false,cue;
 const on=(node,type,fn,options)=>{if(!node)return;node.addEventListener(type,fn,options);cleanups.push(()=>node.removeEventListener(type,fn,options));};
 const updateMedia=()=>root?.querySelectorAll('video').forEach(video=>{
  if(visible&&!finished&&!document.hidden&&!reduce.matches){
   if(video.dataset.src&&!video.src)video.src=video.dataset.src;
   video.play().catch(()=>{});
  }else video.pause();
 });
 const sync=()=>{
  host.dataset.sourceVisible=String(visible&&!document.hidden);
  controller?.visibility?.(visible&&!document.hidden);
  updateMedia();
 };
 const ensure=()=>loading ||= (async()=>{
  try{
   root=host.shadowRoot||host.attachShadow({mode:'open'});
   root.replaceChildren();
   root.append(host.querySelector('template').content.cloneNode(true));
   const style=document.createElement('style');
   style.textContent=':host([data-source-visible="false"]) *,:host([data-playback="complete"]) *{animation-play-state:paused!important}';
   root.append(style);
   gsapPromise ||= import(new URL(host.dataset.gsap,document.baseURI).href);
   const [{gsap},component]=await Promise.all([gsapPromise,import(new URL(host.dataset.module,document.baseURI).href)]);
   if(dead)return;
   cue=createCue(host.closest('[data-explorer-panel]'),host,gsap);
   const ctx={root,host,gsap,on,cue,media:url=>assets[url]||url,reduce,article:host.closest('[data-source-demo]'),status:{textContent:''},replay:{disabled:false},range:{value:0},paused:()=>!visible,isComplete:()=>finished,
    threeUrl:new URL(host.dataset.three,document.baseURI).href,
    engage(){finished=false;host.dataset.playback='interactive';cue.reset();updateMedia();},
    complete(){finished=true;host.dataset.playback='complete';updateMedia();}};
   controller=await component.mount(ctx);
   if(dead){controller?.destroy?.();return;}
   host.dataset.ready='true';sync();
  }catch(error){host.dataset.error='true';console.error(`ONE ${host.dataset.oneSource} experience:`,error);}
 })();
 return{
  ensure,
  setVisible(next){visible=next;if(visible&&!host.dataset.playback)host.dataset.playback=reduce.matches?'settled':'playing';if(visible)ensure();sync();},
  resize(){controller?.resize?.();},
  restart(){finished=false;host.dataset.playback=reduce.matches?'settled':'playing';cue?.reset();if(controller){controller.restart?.();sync();}else ensure();},
  motion(){finished=false;host.dataset.playback=reduce.matches?'settled':'playing';controller?.motion?.();sync();},
  destroy(){dead=true;visible=false;updateMedia();controller?.destroy?.();cue?.destroy();cleanups.forEach(fn=>fn());}
 };
}
function mountExplorer(el){
 const stage=el.querySelector('[data-explorer-stage]');
 const tabs=[...el.querySelectorAll('[data-explorer-tab]')],panels=[...el.querySelectorAll('[data-explorer-panel]')],copy=[...el.querySelectorAll('[data-explorer-copy]')];
 const assets=JSON.parse(el.closest('[data-one-cs]').querySelector('[data-one-source-assets]').textContent);
 const moments=panels.map(panel=>createMoment(panel.querySelector('[data-one-source]'),assets));
 const cleanups=[];
 let active=0,inView=false,idle=0,idleVersion=0,dead=false,stageWidth=0,stageHeight=0;
 const on=(node,type,fn,options)=>{node.addEventListener(type,fn,options);cleanups.push(()=>node.removeEventListener(type,fn,options));};
 const sync=()=>moments.forEach((moment,i)=>moment.setVisible(inView&&i===active));
 const cancelWarmup=()=>{if(!idle)return;if('cancelIdleCallback'in window)window.cancelIdleCallback(idle);else clearTimeout(idle);idle=0;};
 function warmNext(){
  const version=++idleVersion;cancelWarmup();
  const prepare=()=>{if(!dead&&version===idleVersion&&inView&&active<4)moments[active+1].ensure();};
  idle='requestIdleCallback'in window?window.requestIdleCallback(prepare,{timeout:900}):setTimeout(prepare,250);
 }
 const mascot=el.querySelector('[data-explorer-mascot]');
 function select(index){
  index=clamp(index,0,4);if(index===active)return;
  if(mascot&&!reduce.matches){mascot.classList.remove('is-reacting');void mascot.offsetWidth;mascot.classList.add('is-reacting');}
  if(panels[active].contains(document.activeElement))tabs[index].focus({preventScroll:true});
  active=index;el.dataset.activeChapter=String(index);
  const row=tabs[index].parentElement;
  if(row.scrollWidth>row.clientWidth)row.scrollTo({left:tabs[index].offsetLeft-row.offsetLeft-(row.clientWidth-tabs[index].offsetWidth)/2,behavior:reduce.matches?'instant':'smooth'});
  tabs.forEach((tab,i)=>{tab.setAttribute('aria-selected',String(i===index));tab.tabIndex=i===index?0:-1;});
  panels.forEach((panel,i)=>{panel.classList.toggle('is-active',i===index);panel.setAttribute('aria-hidden',String(i!==index));panel.inert=i!==index;});
  copy.forEach((item,i)=>{item.classList.toggle('is-active',i===index);item.setAttribute('aria-hidden',String(i!==index));});
  el.querySelector('[data-explorer-count]').innerHTML=`0${index+1} <span>/ 05</span>`;
  sync();warmNext();
 }
 tabs.forEach((tab,index)=>{
  on(tab,'click',()=>select(index));
  on(tab,'keydown',event=>{
   let next=index;
   if(event.key==='ArrowRight')next=(index+1)%5;
   else if(event.key==='ArrowLeft')next=(index+4)%5;
   else if(event.key==='Home')next=0;
   else if(event.key==='End')next=4;
   else return;
   event.preventDefault();tabs[next].focus({preventScroll:true});select(next);
  });
 });
 on(el.querySelector('[data-explorer-replay]'),'click',()=>moments[active].restart());
 tabs.forEach(tab=>tab.disabled=false);el.querySelector('[data-explorer-replay]').disabled=false;el.dataset.explorerReady='true';
 on(document,'visibilitychange',sync);
 on(reduce,'change',()=>moments.forEach(moment=>moment.motion()));
 // This observer controls playback only; it never selects a chapter or moves the page.
 const visibility=new IntersectionObserver(entries=>{
  const entry=entries[0];inView=entry.isIntersecting&&entry.intersectionRatio>=.12;
  sync();if(inView)warmNext();
 },{threshold:[0,.12]});visibility.observe(stage);
 const resize=new ResizeObserver(()=>{
  const width=stage.clientWidth,height=stage.clientHeight;
  if(width!==stageWidth||height!==stageHeight){stageWidth=width;stageHeight=height;moments.forEach(moment=>moment.resize());}
 });resize.observe(stage);
 return{destroy(){dead=true;++idleVersion;visibility.disconnect();resize.disconnect();cleanups.forEach(fn=>fn());moments.forEach(moment=>moment.destroy());cancelWarmup();}};
}

function boot(scope=document){scope.querySelectorAll('[data-one-explorer]').forEach(el=>{if(!explorers.has(el))explorers.set(el,mountExplorer(el));});}
boot();
document.addEventListener('shopify:section:load',event=>boot(event.target));
document.addEventListener('shopify:section:unload',event=>{for(const [el,instance]of explorers)if(event.target.contains(el)){instance.destroy();explorers.delete(el);}});
